import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import HiveIcon from "@/assets/icons/bees/hive-icon.tsx"
import ArrowDown from "@/assets/icons/misc/arrow-down.tsx"
import CloseCrossIcon from "@/assets/icons/misc/close-cross-icon.tsx"
import CircleTickIcon from "@/assets/icons/task/circle-tick-icon.tsx"
import { type RenderFunctionDrawerItemComponentProps } from "@/components/render-function-drawer/render-function-drawer.tsx"
import Skeleton from "@/components/skeleton/skeleton.tsx"
import { useGetHiveByIdQuery } from "@/features/bees/api/hives-api.ts"
import styles from "@/features/tasks/components/associated-list/associated-list.module.css"
import { cn } from "@/utils/styles.ts"

export const HiveCard: React.FC<RenderFunctionDrawerItemComponentProps> = ({
  id,
  onDelete,
  isSelected,
  onSelect,
}) => {
  const { data: workingHive, isLoading, isError } = useGetHiveByIdQuery(id)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const navigateToHive = () =>
    navigate(`/apiaries/${workingHive?.apiary}/hives/${workingHive?.id}`)

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation()
    onDelete && onDelete(id)
  }

  const handleHiveClick = () => {
    onSelect && onSelect()
    !onSelect && navigateToHive()
  }

  if (isLoading || isError)
    return <Skeleton className={"h-[55px] w-full rounded-[10px]"} />

  return (
    <div
      onClick={handleHiveClick}
      className={cn(
        "flex items-center justify-between gap-2 rounded-[10px] border border-gray-200 bg-[var(--light-grey)] p-2 text-neutral-700",
        isSelected && "border border-brown-500 bg-white",
      )}
    >
      <div
        className={cn(
          "grid w-[70%] grid-cols-[30px_auto] items-center gap-2",
          onSelect && "w-full grid-cols-[30px_30px_auto]",
        )}
      >
        {onSelect && (
          <>
            {isSelected ? (
              <CircleTickIcon />
            ) : (
              <div
                className={
                  "border-dark-grey box-border h-[24px] w-[24px] rounded-full border bg-[var(--background-color)] pr-3"
                }
              ></div>
            )}
          </>
        )}
        <div
          className={cn(
            "grid h-[30px] w-[30px] place-content-center rounded-[4px] bg-gray-600 text-white opacity-60",
            isSelected && "bg-brown-500",
          )}
        >
          <HiveIcon className={cn("scale-150")} />
        </div>
        <div className={"flex w-[70%] flex-col"}>
          <div className={"text-[14px]"}>{workingHive?.code}</div>
          <div className={"truncate text-neutral-700"}>
            {workingHive?.bee_count} {t("bees")}
          </div>
        </div>
      </div>
      {onDelete && (
        <div onClick={handleDelete}>
          <CloseCrossIcon className={"text-neutral-500"} />
        </div>
      )}
    </div>
  )
}

interface Props {
  hives: string[]
  onDelete: (hiveId: string[]) => void
}

export const AssociatedHivesList: React.FC<Props> = ({ hives, onDelete }) => {
  const { t } = useTranslation()
  const [listLimit, setListLimit] = React.useState(3)
  const expandList = () => setListLimit(hives.length)
  const collapseList = () => setListLimit(3)
  const list = hives.slice(0, listLimit)

  const handleDelete = (hiveId: string) => {
    const newHives = hives.filter((hive) => hive !== hiveId)
    onDelete(newHives)
  }

  return (
    <div className={"flex flex-col gap-1"}>
      {list.map((hive, index) => (
        <HiveCard id={hive} key={index} onDelete={handleDelete} />
      ))}
      {hives.length > listLimit && (
        <div
          className={"flex w-full items-center justify-center gap-2"}
          onClick={expandList}
        >
          <i className={styles["more-items"]}>{t("seeOtherHivesAssociated")}</i>
          <div className={"scale-125"}>
            <ArrowDown />
          </div>
        </div>
      )}
      {hives.length === listLimit && hives.length > 3 && (
        <div
          className={"flex w-full items-center justify-center gap-2"}
          onClick={collapseList}
        >
          <i className={styles["more-items"]}>
            {t("hideOtherHivesAssociated")}
          </i>
          <div className={"scale-125"}>
            <ArrowDown isDown={false} />
          </div>
        </div>
      )}
    </div>
  )
}
